/**
 * Proposals and contracts: what a signed document says the client owes, and
 * how that splits into draws.
 *
 * A proposal is one document covering one or more *segments* — the PDF this
 * was modelled on quotes a kitchen and a walking closet together, each with
 * its own payment schedule, under a single signature. A segment is what
 * becomes a project when the contract is approved, which is why its total and
 * its schedule are resolved here rather than left to the route: approval has
 * to produce exactly the numbers the client signed, and the only way to
 * guarantee that is for both sides to call the same function.
 *
 * The residual rule is `resolveSchedule`'s, deliberately. Fixed-amount rows
 * come off the top, percentage rows split what is left through
 * `splitByWeights`, and the last one absorbs the rounding — so a 50/40/10
 * split of $29,500 reconciles to the cent instead of leaving a stray penny
 * that turns up months later as an invoice that will not close.
 */
import type { MilestoneBasis } from "./billing";
import {
  type Money,
  type Rate,
  ZERO,
  isZeroRate,
  money,
  mulQty,
  ratio,
  splitByWeights,
  sub,
  sum,
  sumRates,
} from "./money";

export class ContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ContractError";
  }
}

/** One row of the INVESTMENT table. */
export interface ProposalItem {
  readonly id: string;
  readonly seq: number;
  readonly description: string;
  /** Decimal string, like every other quantity in the system. */
  readonly quantity: string;
  /** The line's price to the client, already extended for quantity. */
  readonly amount: Money;
}

/** One row of a segment's PAYMENT SCHEDULE. Becomes a `billing_milestones`
 *  row on approval, which is why it carries the same basis pair. */
export interface ProposalScheduleRow {
  readonly id: string;
  readonly seq: number;
  readonly name: string;
  readonly basis: MilestoneBasis;
  readonly percent: Rate | null;
  readonly amount: Money | null;
}

/** One project-to-be inside the contract. */
export interface ProposalSegment {
  readonly id: string;
  readonly seq: number;
  readonly name: string;
  readonly items: readonly ProposalItem[];
  readonly schedule: readonly ProposalScheduleRow[];
}

export interface ResolvedProposalRow {
  readonly id: string;
  readonly seq: number;
  readonly name: string;
  readonly basis: MilestoneBasis;
  readonly amount: Money;
  /** What this row actually works out to as a share of the segment — shown
   *  beside a fixed-amount row so a mixed schedule still reads as percentages
   *  the way the client's copy does. */
  readonly share: Rate;
}

export interface ResolvedProposalSegment {
  readonly id: string;
  readonly seq: number;
  readonly name: string;
  /** Sum of the segment's investment items: the contract value of the project
   *  this segment becomes. */
  readonly total: Money;
  readonly rows: readonly ResolvedProposalRow[];
  readonly scheduledTotal: Money;
  /**
   * Segment value that no schedule row absorbs. Non-zero means the schedule is
   * short — it is surfaced rather than silently spread, because a proposal
   * that bills less than it quotes is a mistake somebody has to see before it
   * goes out.
   */
  readonly unscheduled: Money;
}

export interface ResolvedProposal {
  readonly segments: readonly ResolvedProposalSegment[];
  /** Everything the client is agreeing to pay across every segment. */
  readonly total: Money;
  readonly scheduledTotal: Money;
  readonly unscheduled: Money;
}

/** Extend one investment line. Kept here so the editor, the rendered document
 *  and approval all price a line the same way. */
export function itemAmount(unitPrice: Money, quantity: string): Money {
  return mulQty(unitPrice, quantity);
}

export function segmentTotal(items: readonly ProposalItem[]): Money {
  return sum(items.map((i) => i.amount));
}

export function resolveProposalSegment(segment: ProposalSegment): ResolvedProposalSegment {
  const total = segmentTotal(segment.items);
  const rows = [...segment.schedule].sort((a, b) => a.seq - b.seq);

  const fixed = rows.filter((r) => r.basis === "amount");
  const percent = rows.filter((r) => r.basis === "percent");

  for (const r of fixed) {
    if (r.amount === null) {
      throw new ContractError(`schedule row ${r.id} has basis "amount" but no amount`);
    }
  }
  for (const r of percent) {
    if (r.percent === null) {
      throw new ContractError(`schedule row ${r.id} has basis "percent" but no percent`);
    }
  }

  const pool = sub(total, sum(fixed.map((r) => r.amount as Money)));
  const weights = percent.map((r) => r.percent as Rate);
  const canSplit = percent.length > 0 && !isZeroRate(sumRates(weights));
  const percentAmounts = canSplit ? splitByWeights(pool, weights) : [];

  const byId = new Map<string, Money>();
  for (const r of fixed) byId.set(r.id, money(r.amount as Money));
  percent.forEach((r, i) => byId.set(r.id, percentAmounts[i] ?? ZERO));

  const resolved: ResolvedProposalRow[] = rows.map((r) => {
    const amount = byId.get(r.id) ?? ZERO;
    return {
      id: r.id,
      seq: r.seq,
      name: r.name,
      basis: r.basis,
      amount,
      share: ratio(amount, total),
    };
  });

  const scheduledTotal = sum(resolved.map((r) => r.amount));

  return {
    id: segment.id,
    seq: segment.seq,
    name: segment.name,
    total,
    rows: resolved,
    scheduledTotal,
    unscheduled: sub(total, scheduledTotal),
  };
}

/** One row of a merged PAYMENT SCHEDULE block — several segments' draws shown
 *  as a single table on the document, because a client reading a proposal for
 *  a kitchen and a closet often wants one "Deposit" line, not two. */
export interface CombinedScheduleRow {
  readonly id: string;
  readonly name: string;
  readonly amount: Money;
  readonly share: Rate;
}

export interface CombinedSchedule {
  readonly rows: readonly CombinedScheduleRow[];
  readonly total: Money;
}

/**
 * Several segments' resolved schedules, folded into one table for display.
 *
 * Rows are matched by name, in the order each name first appears — the
 * ordinary case is every segment starting from the same template schedule
 * ("Deposit / contract signing", "Delivery / installation", "Final
 * completion"), so "Deposit" across a kitchen and a closet becomes one row
 * worth both amounts. A name that only exists on some of the merged segments
 * still gets a row; it is simply the sum of whichever segments have it.
 *
 * This never changes what gets billed. Each segment's rows still resolve
 * against that segment's own total and still become that project's own
 * milestones on approval — merging only changes how the numbers are grouped
 * on the page the client reads.
 */
export function combineSchedules(segments: readonly ResolvedProposalSegment[]): CombinedSchedule {
  const total = sum(segments.map((s) => s.total));
  const order: string[] = [];
  const amountsByName = new Map<string, Money[]>();
  for (const seg of segments) {
    for (const row of seg.rows) {
      if (!amountsByName.has(row.name)) {
        amountsByName.set(row.name, []);
        order.push(row.name);
      }
      amountsByName.get(row.name)?.push(row.amount);
    }
  }

  const rows = order.map((name) => {
    const amount = sum(amountsByName.get(name) as Money[]);
    return { id: name, name, amount, share: ratio(amount, total) };
  });

  return { rows, total };
}

export function resolveProposal(segments: readonly ProposalSegment[]): ResolvedProposal {
  const resolved = [...segments]
    .sort((a, b) => a.seq - b.seq)
    .map((s) => resolveProposalSegment(s));

  return {
    segments: resolved,
    total: sum(resolved.map((s) => s.total)),
    scheduledTotal: sum(resolved.map((s) => s.scheduledTotal)),
    unscheduled: sum(resolved.map((s) => s.unscheduled)),
  };
}
